import GameFields from "@/components/Spellify/GameFields/GameFields"
import styles from './spellify.module.scss'
import { ScryfallCard } from "@scryfall/api-types";
import { useEffect } from "react";
import CHGamesHead from "@/components/Layout/CHGamesHead/CHGamesHead";
// @ts-ignore
import spellifyMetaImage from '@/assets/images/spellify_preview.png'
import CryptoJS from 'crypto-js'
import {useRouter} from "next/router";
import {hidePreview, showPreview} from "@/utils/hoverZoomExtension";
import {SponsorshipType} from "@/types/spellify/sponshorship";
import FLSProvider, {FLSLoader, useFLS} from "@/utils/FLSProvider";
import {buttonMapType} from "@/types/spellify/ButtonMapType";
import {GameStatsType} from "@/types/spellify/stats";

export const KEY = 'spellify'

type Props = {
  encryptedDailyCard: string
  spellifyNumber: number
  encryptedHardCard: string
  hardSpellifyNumber: number
  sponsorship: SponsorshipType | null
  encryptedSaltCards?: string
  saltNumber?: number
}

export type SpellifyStateType = {
  spellifyCard: ScryfallCard.Normal | null
  spellifyHardCard: ScryfallCard.Normal | null
  spellifyButtonMap: buttonMapType
  spellifyButtonMapHard: buttonMapType
  spellifyGuesses: string[]
  spellifyGuessesHard: string[]
  spellifyCardGuesses: string[][]
  spellifyCardGuessesHard: string[][]
  spellifyGameStats: GameStatsType | null
  spellifyGameStatsHard: GameStatsType | null
  spellifyPlayedToday: boolean
  spellifyPlayedTodayHard: boolean
  spellifyGameState: 'guessing' | 'success' | 'failure' | null
  spellifyGameStateHard: 'guessing' | 'success' | 'failure' | null
  spellifyHasReadRules: boolean
  spellifyNumber: number
  // Salt mode is five cards a day, so its state is stored per card index
  spellifySaltNumber: number
  spellifySaltIndex: number
  spellifySaltButtonMaps: buttonMapType[]
  spellifySaltGuesses: string[][]
  spellifySaltCardGuesses: string[][][]
  spellifySaltGameStates: ('guessing' | 'success' | 'failure')[]
  spellifySaltPlayed: boolean[]
  spellifySaltGameStats: GameStatsType | null
}
const spellifyDefaultState: SpellifyStateType = {
  spellifyCard:  null,
  spellifyHardCard:  null,
  spellifyButtonMap: {},
  spellifyButtonMapHard: {},
  spellifyGuesses: [],
  spellifyGuessesHard: [],
  spellifyCardGuesses: [],
  spellifyCardGuessesHard: [],
  spellifyGameStats: null,
  spellifyGameStatsHard: null,
  spellifyPlayedToday: false,
  spellifyPlayedTodayHard: false,
  spellifyGameState: null,
  spellifyGameStateHard: null,
  spellifyHasReadRules: false,
  spellifyNumber: 0,
  spellifySaltNumber: 0,
  spellifySaltIndex: 0,
  spellifySaltButtonMaps: [],
  spellifySaltGuesses: [],
  spellifySaltCardGuesses: [],
  spellifySaltGameStates: [],
  spellifySaltPlayed: [],
  spellifySaltGameStats: null,
}

const SpellifyPage = ({encryptedDailyCard, spellifyNumber, encryptedHardCard, hardSpellifyNumber, sponsorship, encryptedSaltCards, saltNumber}: Props) => {

  const router = useRouter()
  const isInfinite = router.pathname.includes('infinite')

  const dailyCard = JSON.parse(
    CryptoJS.AES.decrypt(encryptedDailyCard, KEY).toString(CryptoJS.enc.Utf8)
  )

  const hardCard = JSON.parse(
    CryptoJS.AES.decrypt(encryptedHardCard, KEY).toString(CryptoJS.enc.Utf8)
  )

  const saltCards: ScryfallCard.Normal[] | null = encryptedSaltCards
    ? JSON.parse(CryptoJS.AES.decrypt(encryptedSaltCards, KEY).toString(CryptoJS.enc.Utf8))
    : null

  useEffect(() => {
    hidePreview()
    return () => {
      showPreview()
    }
  }, [])

  return (
    <div className={styles.spellifyContainer}>
      <CHGamesHead title={router.pathname.includes('/hard') ? 'Spellify Hard Mode' : router.pathname.includes('/salt') ? 'Spellify Salt' : 'Spellify'}
                   description={`A Magic: the Gathering hangman-style word game by EDHREC`}
                   imageUrl={spellifyMetaImage.src}/>
      {isInfinite && <div id="mediavine-settings" data-blocklist-all="1" data-expires-at="2025-11-23"/>}
      <FLSProvider database="ch-games" collection="userDataSpellify" defaultState={spellifyDefaultState}>
        <FLSLoader>
          <GameFields sponsorship={sponsorship} dailyCard={dailyCard} spellifyNumber={spellifyNumber}
                                  hardDailyCard={hardCard} hardSpellifyNumber={hardSpellifyNumber}
                                  saltCards={saltCards} saltNumber={saltNumber}/>
        </FLSLoader>
      </FLSProvider>
    </div>
  )
}

export const useSpellifyState = <
  K extends keyof SpellifyStateType
>(
  key: K,
  fallback?: SpellifyStateType[K]
) => useFLS<SpellifyStateType, K>(key, fallback);


export const getServerSideProps = async () => {
  const {card, cardData, spellifyNumber, hardCard, hardCardData, hardSpellifyNumber, sponsorship} = (await fetch("https://getspellifydailycardplus-hpca4snkqq-uc.a.run.app").then(res => res.json()))

  const dailyCard: ScryfallCard.Normal = cardData
  const dailyHardCard = hardCardData

  if (card === process.env.PREVIEW_CARD) {
    if (!dailyCard.image_uris) return
    dailyCard.image_uris = {
      ...dailyCard.image_uris,
      normal: process.env.PREVIEW_CARD_FULL || '',
      large: process.env.PREVIEW_CARD_FULL || '',
      art_crop: process.env.PREVIEW_CARD_CROP || ''
    }
  }

  return {
    props: {
      encryptedDailyCard: CryptoJS.AES.encrypt(JSON.stringify(dailyCard), KEY).toString(),
      encryptedHardCard: CryptoJS.AES.encrypt(JSON.stringify(dailyHardCard), KEY).toString(),
      spellifyNumber,
      hardSpellifyNumber,
      sponsorship
    }
  }

}

export default SpellifyPage